import { useMemo, useState } from 'react';

import { NfcCardOrderProduct } from 'lib/models/nfcCards';

import usePopulatedSections from 'hooks/usePopulatedSections';
import useProductCategories from 'hooks/useProductCategories';
import useProducts from 'hooks/useProducts';

/** What was picked of a product: how many, and with which mixer when it takes one. */
export type NfcCardCartLine = { quantity: number; mixer: string | null };

/** The whole catalogue in one page, as the category modal reads it. */
const allProducts = {
	page: 0,
	rowsPerPage: 50000,
	order: 'asc' as 'asc',
	orderBy: 'title',
};

/**
 * The products to load on cards: a section of the catalogue and what is picked of it, as
 * the cart a bar order carries. Shared by the load of products on existing cards and by the
 * template of a new batch, which show it with `NfcCardCartPicker`.
 *
 * The cart is built with the catalogue's own price and title, because the API prices it
 * again and refuses a line that does not match. `total` is only an estimate: the API
 * answers the one it priced.
 */
function useNfcCardCart() {
	const [sectionId, setSectionId] = useState<string>('');
	const [lines, setLines] = useState<Record<string, NfcCardCartLine>>({});
	const [search, setSearch] = useState('');

	const { loading: loadingSections, sections, refetch: refetchSections } = usePopulatedSections();
	const { loading: loadingProducts, products, refetch: refetchProducts } = useProducts(allProducts);
	const { loading: loadingCategories, productCategoriesMap } = useProductCategories();

	const loading = loadingSections || loadingProducts || loadingCategories;

	const section = sections.find((element) => element._id === sectionId);

	// What the section sells, which is all the API accepts for it. A product without a
	// price cannot be sold, so it is not offered.
	const sectionProducts = useMemo(() => {
		const categories = new Set((section?.categories ?? []).map((category) => `${category._id}`));

		return products.filter(
			(product) =>
				!!product._id &&
				typeof product.price === 'number' &&
				product.categories.some((category) => categories.has(category))
		);
	}, [section, products]);

	const cart: NfcCardOrderProduct[] = sectionProducts
		.filter((product) => (lines[`${product._id}`]?.quantity ?? 0) > 0)
		.map((product) => {
			const line = lines[`${product._id}`];
			return {
				productId: `${product._id}`,
				title: product.title,
				price: product.price ?? 0,
				quantity: line.quantity,
				// The API requires the mixer quantities to add up to the line quantity, and a
				// line here has a single mixer
				mixers: line.mixer ? [{ mixer: line.mixer, quantity: line.quantity }] : null,
			};
		});

	const total = cart.reduce((sum, line) => sum + line.price * line.quantity, 0);

	const missingMixer = sectionProducts.some((product) => {
		const line = lines[`${product._id}`];
		return !!line && line.quantity > 0 && !!product.mixer?.elements.length && !product.mixer.optional && !line.mixer;
	});

	const selectSection = (value: string) => {
		// Products of another section would be refused, so the cart starts over
		setSectionId(value);
		setLines({});
	};

	const updateLine = (product: Product, change: Partial<NfcCardCartLine>) => {
		setLines((current) => {
			const previous = current[`${product._id}`] ?? { quantity: 0, mixer: null };
			return { ...current, [`${product._id}`]: { ...previous, ...change } };
		});
	};

	/** Reads the catalogue again, after the API refused a cart that no longer matches it. */
	const refetchCatalogue = () => {
		refetchSections();
		refetchProducts();
	};

	return {
		loading,
		loadingProducts,
		sections,
		section,
		sectionProducts,
		productCategoriesMap,
		lines,
		search,
		setSearch,
		selectSection,
		updateLine,
		cart,
		total,
		missingMixer,
		refetchCatalogue,
	};
}

export type NfcCardCart = ReturnType<typeof useNfcCardCart>;

export default useNfcCardCart;
